import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  IconArrowLeft,
  IconPencil,
  IconTrash,
  IconBan,
  IconCheck,
  IconX
} from "@tabler/icons-react";
import Logo from "../../assets/images/paqtory.png";
import AdminLayout from "../../components/common/AdminLayout";
// Redux
import { useSelector, useDispatch } from "react-redux";
import {
  fetchProductDetails,
  updateProduct,
  getAllProductDefaultProperty,
  toggleProductStat,
  fetchCategoriesAndSubcategories
} from "../../redux/slices/productsSlice";
import Sidebar from "../../components/common/AdminSideBar";
import Header from "../../components/common/AdminHeader";
import { useAdminLayout } from "../../context/adminLayoutUseContext";
import LoadingFallback from "../../components/common/LoadingFallback";
import ErrorFallback from "../../components/common/ErrorFallback";

const ProductDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { setLayoutConfig } = useAdminLayout();

  // Redux state
  const {
    currentProduct,
    loading,
    error,
    productDefaultProperties,
    categories,
    subcategories,
    categoriesLoading
  } = useSelector((state) => state.products);

  // Local state
  const [formData, setFormData] = useState({
    product_name: "",
    price: "",
    stock: "",
    status: "ACTIVE",
    category: "",
    sub_category: "",
    available_sizes: [],
    available_colors: [],
    available_printing_methods: [],
    available_fabric_materials: [],
    customization_options: [],
    orientation_options: [],
    corners: []
  });
  const [isEditing, setIsEditing] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showStatusModal, setShowStatusModal] = useState(false);
  const [localStatus, setLocalStatus] = useState("ACTIVE");
  const [filteredSubcategories, setFilteredSubcategories] = useState([]);

  // Fetch product details, default properties, and categories on mount
  useEffect(() => {
    setLayoutConfig({
      title: "Product Management / Edit",
      showBackButton: true,
      scrollable: true,
    });
    dispatch(fetchProductDetails(id));
    dispatch(getAllProductDefaultProperty());
    dispatch(fetchCategoriesAndSubcategories());

  }, [dispatch, id, setLayoutConfig]);



  useEffect(() => {
    if (currentProduct) {
      const newFormData = {
        product_name: currentProduct.product_name || "",
        price: currentProduct.price || "",
        stock: currentProduct.stock || "",
        status: currentProduct.status || "ACTIVE",
        category: currentProduct.category?.id || "",
        sub_category: currentProduct.sub_category?.id || "",
        available_sizes: currentProduct.available_sizes || [],
        available_colors: currentProduct.available_colors || [],
        available_printing_methods: currentProduct.available_printing_methods || [],
        available_fabric_materials: currentProduct.available_fabric_materials || [],
        customization_options: currentProduct.customization_options || [],
        orientation_options: currentProduct.orientation_options || [],
        corners: currentProduct.corners || [],
        is_featured: currentProduct.is_featured || false,
      };
      setFormData(newFormData);
      setLocalStatus(currentProduct.status || "ACTIVE");

      // Update filtered subcategories based on initial category
      if (newFormData.category) {
        const filtered = subcategories.filter(sub => sub.category === newFormData.category);

        setFilteredSubcategories(filtered);
      } else {
        setFilteredSubcategories([]);
      }
    }
  }, [currentProduct]); // Remove subcategories from dependencies


  // Create a function to get subcategories for a specific category
  const getSubcategoriesForCategory = (categoryId) => {

    return subcategories.filter(sub => toString(sub.category).toString() === toString(categoryId).trim());
  };



  const handleInputChange = (e) => {
    const { name, value } = e.target;

    setFormData(prev => {
      const newData = { ...prev, [name]: value };

      // When category changes, reset subcategory and update filtered subcategories

      if (name === 'category') {
        newData.sub_category = "";
        const filtered = getSubcategoriesForCategory(value);
        setFilteredSubcategories(filtered);
      }
      return newData;
    });
  };



  const handleCheckboxChange = (name, value) => {
    setFormData(prev => {
      const currentValues = prev[name];
      const newValues = currentValues.includes(value)
        ? currentValues.filter(item => item !== value)
        : [...currentValues, value];
      return { ...prev, [name]: newValues };
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await dispatch(updateProduct({
        id,
        productData: formData
      })).unwrap();
      setIsEditing(false);
      dispatch(fetchProductDetails(id));
    } catch (error) {
      console.error('Failed to update product:', error);
    }
  };

  const handleStatusToggle = async () => {
    try {
      const result = await dispatch(toggleProductStat(id)).unwrap();
      const newStatus = result.status;
      setLocalStatus(newStatus);
      setFormData(prev => ({ ...prev, status: newStatus }));
      setShowStatusModal(false);
    } catch (error) {
      console.error('Failed to toggle product status:', error);
    }
  };

  const handleDelete = async () => {
    try {
      // TODO: Implement delete functionality
      setShowDeleteModal(false);
      navigate('/admin/products');
    } catch (error) {
      console.error('Failed to delete product:', error);
    }
  };

  // Get current category and subcategory names for display
  // const currentCategoryName = categories.find(cat => cat.id === formData.category)?.category_name || "";
  // const currentSubcategoryName = subcategories.find(sub => sub.id === formData.sub_category)?.sub_category_name || "";
  const currentCategoryName = categories.find(cat => cat.id === formData.category)?.category_name || "";
  const currentSubcategoryName = filteredSubcategories.find(sub => sub.id === formData.sub_category)?.sub_category_name || "";

  if (loading) return <LoadingFallback />;
  if (error) return <ErrorFallback error={error} onRetry={() => dispatch(fetchProductDetails(id))} />;

  return (
    <div>

      <main className="py-3 py-2 flex-1 overflow-y-auto">
        <div className="bg-white rounded-lg shadow-md p-6">
          <form onSubmit={handleSubmit}>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Basic Information */}
              <div className="space-y-4">
                <h3 className="text-lg font-semibold border-b pb-2">Basic Information</h3>

                <div>
                  <label className="block text-sm font-medium mb-1">Product Name</label>
                  <input
                    type="text"
                    name="product_name"
                    value={formData.product_name}
                    onChange={handleInputChange}
                    className="w-full border rounded px-3 py-2"
                    disabled={!isEditing}
                  />
                </div>

                {/* <div className="flex items-center space-x-2 mb-4">
                  <label className="text-sm font-medium">Is Featured</label>
                  <button
                    type="button"
                    onClick={() => setFormData(prev => ({ ...prev, is_featured: !prev.is_featured }))}
                    className={`w-12 h-6 flex items-center rounded-full p-1 ${formData.is_featured ? 'bg-blue-500' : 'bg-gray-300'
                      }`}
                    disabled={!isEditing}
                  >
                    <div
                      className={`w-4 h-4 bg-white rounded-full shadow-md transform ${formData.is_featured ? 'translate-x-6' : ''
                        }`}
                    ></div>
                  </button>
                </div> */}
                <div className="flex items-center space-x-2 mb-4">
                  <label className="text-sm font-medium">Is Featured</label>
                  <button
                    type="button"
                    onClick={() => setFormData(prev => ({ ...prev, is_featured: !prev.is_featured }))}
                    className={`w-12 h-6 flex items-center rounded-full p-1 ${formData.is_featured ? 'bg-blue-500' : 'bg-gray-300'
                      }`}
                    disabled={!isEditing}
                  >
                    <div
                      className={`w-4 h-4 bg-white rounded-full shadow-md transform ${formData.is_featured ? 'translate-x-6' : ''
                        }`}
                    ></div>
                  </button>
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Price</label>
                  <input
                    type="number"
                    name="price"
                    value={formData.price}
                    onChange={handleInputChange}
                    className="w-full border rounded px-3 py-2"
                    disabled={!isEditing}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Stock</label>
                  <input
                    type="number"
                    name="stock"
                    value={formData.stock}
                    onChange={handleInputChange}
                    className="w-full border rounded px-3 py-2"
                    disabled={!isEditing}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium mb-1">Status</label>
                  <div className={`px-3 py-2 rounded ${localStatus === 'ACTIVE' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                    }`}>
                    {localStatus === 'ACTIVE' ? 'Active' : 'Inactive'}
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium mb-1">Category</label>
                  {isEditing ? (
                    <>
                      <select
                        name="category"
                        value={formData.category}
                        onChange={handleInputChange}
                        className="w-full border rounded px-3 py-2"
                        disabled={!isEditing || categoriesLoading}
                      >
                        <option value="">Select Category</option>
                        {categories.map(category => (
                          <option key={category.id} value={category.id}>
                            {category.category_name}
                          </option>
                        ))}
                      </select>
                      {formData.category && (
                        <p className="text-xs text-gray-500 mt-1">
                          Current: {currentCategoryName}
                        </p>
                      )}
                    </>
                  ) : (
                    <div className="px-3 py-2 border rounded bg-gray-100">
                      {currentCategoryName || '-'}
                    </div>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium mb-1">Sub Category</label>
                  {isEditing ? (
                    <>
                      <select
                        name="sub_category"
                        value={formData.sub_category}
                        onChange={handleInputChange}
                        className="w-full border rounded px-3 py-2"
                        disabled={!isEditing || !formData.category}
                      >
                        <option value="">Select Subcategory</option>
                        {filteredSubcategories.map(subcategory => (
                          <option key={subcategory.id} value={subcategory.id}>
                            {subcategory.sub_category_name}
                          </option>
                        ))}
                      </select>
                      {formData.sub_category && (
                        <p className="text-xs text-gray-500 mt-1">
                          Current: {currentSubcategoryName}
                        </p>
                      )}
                    </>
                  ) : (
                    <div className="px-3 py-2 border rounded bg-gray-100">
                      {currentSubcategoryName || '-'}
                    </div>
                  )}
                </div>
              </div>

              {/* Product Images */}
              <div className="space-y-4">
                <h3 className="text-lg font-semibold border-b pb-2">Product Images</h3>
                {currentProduct?.template?.media_files?.length > 0 ? (
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <p className="text-sm font-medium mb-2">Front Image</p>
                      <img
                        src={currentProduct.template.media_files[0].front_image}
                        alt="Front"
                        className="w-full h-auto rounded border"
                      />
                    </div>
                    <div>
                      <p className="text-sm font-medium mb-2">Back Image</p>
                      <img
                        src={currentProduct.template.media_files[0].back_image}
                        alt="Back"
                        className="w-full h-auto rounded border"
                      />
                    </div>
                  </div>
                ) : (
                  <p>No images available</p>
                )}
              </div>

              {/* Product Properties */}
              {productDefaultProperties && (
                <>
                  {/* Available Sizes */}
                  <div className="space-y-2">
                    <h3 className="text-lg font-semibold border-b pb-2">Available Sizes</h3>
                    <div className="grid grid-cols-3 gap-2">
                      {productDefaultProperties.product_sizes?.map(size => (
                        <label key={size} className="flex items-center space-x-2">
                          <input
                            type="checkbox"
                            checked={formData.available_sizes.includes(size)}
                            onChange={() => handleCheckboxChange('available_sizes', size)}
                            className="rounded"
                            disabled={!isEditing}
                          />
                          <span>{size}</span>
                        </label>
                      ))}
                    </div>
                  </div>

                  {/* Available Colors */}
                  <div className="space-y-2">
                    <h3 className="text-lg font-semibold border-b pb-2">Available Colors</h3>
                    <div className="grid grid-cols-3 gap-2">
                      {productDefaultProperties.product_colors?.map(color => (
                        <label key={color} className="flex items-center space-x-2">
                          <input
                            type="checkbox"
                            checked={formData.available_colors.includes(color)}
                            onChange={() => handleCheckboxChange('available_colors', color)}
                            className="rounded"
                            disabled={!isEditing}
                          />
                          <span>{color}</span>
                        </label>
                      ))}
                    </div>
                  </div>

                  {/* Printing Methods */}
                  <div className="space-y-2">
                    <h3 className="text-lg font-semibold border-b pb-2">Printing Methods</h3>
                    <div className="grid grid-cols-2 gap-2">
                      {productDefaultProperties.product_printing_methods?.map(method => (
                        <label key={method} className="flex items-center space-x-2">
                          <input
                            type="checkbox"
                            checked={formData.available_printing_methods.includes(method)}
                            onChange={() => handleCheckboxChange('available_printing_methods', method)}
                            className="rounded"
                            disabled={!isEditing}
                          />
                          <span>{method.replace(/_/g, ' ')}</span>
                        </label>
                      ))}
                    </div>
                  </div>

                  {/* Fabric Materials */}
                  <div className="space-y-2">
                    <h3 className="text-lg font-semibold border-b pb-2">Fabric Materials</h3>
                    <div className="grid grid-cols-2 gap-2">
                      {productDefaultProperties.product_fabric_materials?.map(material => (
                        <label key={material} className="flex items-center space-x-2">
                          <input
                            type="checkbox"
                            checked={formData.available_fabric_materials.includes(material)}
                            onChange={() => handleCheckboxChange('available_fabric_materials', material)}
                            className="rounded"
                            disabled={!isEditing}
                          />
                          <span>{material.replace(/_/g, ' ')}</span>
                        </label>
                      ))}
                    </div>
                  </div>

                  {/* Customization Options */}
                  <div className="space-y-2">
                    <h3 className="text-lg font-semibold border-b pb-2">Customization Options</h3>
                    <div className="grid grid-cols-2 gap-2">
                      {productDefaultProperties.product_customization_options?.map(option => (
                        <label key={option} className="flex items-center space-x-2">
                          <input
                            type="checkbox"
                            checked={formData.customization_options.includes(option)}
                            onChange={() => handleCheckboxChange('customization_options', option)}
                            className="rounded"
                            disabled={!isEditing}
                          />
                          <span>{option.replace(/_/g, ' ')}</span>
                        </label>
                      ))}
                    </div>
                  </div>

                  {/* Orientation Options */}
                  <div className="space-y-2">
                    <h3 className="text-lg font-semibold border-b pb-2">Orientation</h3>
                    <div className="grid grid-cols-2 gap-2">
                      {productDefaultProperties.product_orientation_options?.map(option => (
                        <label key={option} className="flex items-center space-x-2">
                          <input
                            type="checkbox"
                            checked={formData.orientation_options.includes(option)}
                            onChange={() => handleCheckboxChange('orientation_options', option)}
                            className="rounded"
                            disabled={!isEditing}
                          />
                          <span>{option}</span>
                        </label>
                      ))}
                    </div>
                  </div>

                  {/* Corners */}
                  <div className="space-y-2">
                    <h3 className="text-lg font-semibold border-b pb-2">Corners</h3>
                    <div className="grid grid-cols-2 gap-2">
                      {productDefaultProperties.product_corners?.map(corner => (
                        <label key={corner} className="flex items-center space-x-2">
                          <input
                            type="checkbox"
                            checked={formData.corners.includes(corner)}
                            onChange={() => handleCheckboxChange('corners', corner)}
                            className="rounded"
                            disabled={!isEditing}
                          />
                          <span>{corner}</span>
                        </label>
                      ))}
                    </div>
                  </div>
                </>
              )}
            </div>

            {/* Form Actions */}
            {isEditing && (
              <div className="mt-6 flex justify-end space-x-4">
                <button
                  type="button"
                  onClick={() => {
                    setIsEditing(false);
                    if (currentProduct) {
                      setFormData({
                        product_name: currentProduct.product_name || "",
                        price: currentProduct.price || "",
                        stock: currentProduct.stock || "",
                        status: currentProduct.status || "ACTIVE",
                        category: currentProduct.category?.id || "",
                        sub_category: currentProduct.sub_category?.id || "",
                        available_sizes: currentProduct.available_sizes || [],
                        available_colors: currentProduct.available_colors || [],
                        available_printing_methods: currentProduct.available_printing_methods || [],
                        available_fabric_materials: currentProduct.available_fabric_materials || [],
                        customization_options: currentProduct.customization_options || [],
                        orientation_options: currentProduct.orientation_options || [],
                        corners: currentProduct.corners || []
                      });
                    }
                  }}
                  className="px-4 py-2 bg-gray-300 text-gray-700 rounded"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-blue-500 text-white rounded"
                >
                  Save Changes
                </button>
              </div>
            )}
          </form>

          <div className="flex justify-center items-center mt-6">
            <div className="flex items-center gap-4">
              {!isEditing && (
                <>
                  <button
                    onClick={() => setIsEditing(true)}
                    className="flex items-center gap-1 bg-blue-500 text-white px-3 py-1 rounded text-lg"
                  >
                    <IconPencil size={16} />
                    Edit
                  </button>

                </>
              )}
            </div>
          </div>

        </div>
      </main>


      {/* Status Toggle Modal */}
      {showStatusModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-96">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold">
                {localStatus === 'ACTIVE' ? 'Deactivate Product' : 'Activate Product'}
              </h3>
              <button onClick={() => setShowStatusModal(false)}>
                <IconX size={20} />
              </button>
            </div>
            <p className="mb-6">
              Are you sure you want to {localStatus === 'ACTIVE' ? 'deactivate' : 'activate'} this product?
            </p>
            <div className="flex justify-end space-x-4">
              <button
                onClick={() => setShowStatusModal(false)}
                className="px-4 py-2 bg-gray-300 rounded"
              >
                Cancel
              </button>
              <button
                onClick={handleStatusToggle}
                className={`px-4 py-2 rounded text-white ${localStatus === 'ACTIVE' ? 'bg-yellow-500' : 'bg-green-500'
                  }`}
              >
                Confirm
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Modal */}
      {showDeleteModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-96">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold">Delete Product</h3>
              <button onClick={() => setShowDeleteModal(false)}>
                <IconX size={20} />
              </button>
            </div>
            <p className="mb-6">
              Are you sure you want to delete this product? This action cannot be undone.
            </p>
            <div className="flex justify-end space-x-4">
              <button
                onClick={() => setShowDeleteModal(false)}
                className="px-4 py-2 bg-gray-300 rounded"
              >
                Cancel
              </button>
              <button
                onClick={handleDelete}
                className="px-4 py-2 bg-red-500 text-white rounded"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProductDetail;